import debounce from './debounce';

const VISIBILITY_THRESHOLD = 0.75;
const VISIBILITY_TOLERANCE = 0.01;
const DEBOUNCE_TIMEOUT_MS = 1500;
const IMPRESSION_KEY = 'data-impression';
const IMPRESSION_KEY_RULE = `[${IMPRESSION_KEY}]`;

const parseImpression = (element) => {
  const [id, list, position] = element.getAttribute('data-impression').split('|');

  return {
    id: Number(id),
    list,
    position: position ? Number(position) : 1,
  };
};

const getImpressionAttr = (element) => element.getAttribute(IMPRESSION_KEY);

class Impressions {
  constructor() {
    this.sentImpressions = new Set();
    this.cache = {};

    this.pendingImpressions = new Map();
    this.visibilityObserver = null;
    this.domObserver = null;

    this.attachListeners();
  }

  addPendingImpression = (impression) => {
    const { list } = parseImpression(impression);
    const impressions = this.pendingImpressions.get(list) || [];

    this.pendingImpressions.set(list, [...impressions, impression]);
  };

  attachListeners = () => {
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isVisibleEnough =
            entry.isIntersecting &&
            entry.intersectionRatio >= VISIBILITY_THRESHOLD - VISIBILITY_TOLERANCE;
          if (isVisibleEnough) {
            const impression = entry.target;
            const key = getImpressionAttr(impression);

            if (this.sentImpressions.has(key)) {
              return;
            }

            this.sentImpressions.add(key);

            this.addPendingImpression(impression);

            // do not observe after it's shown
            this.visibilityObserver.unobserve(impression);

            this.sendImpressionsDebounced();
          }
        });
      },
      {
        threshold: VISIBILITY_THRESHOLD,
      },
    );

    // observe new dynamicly added cards
    this.domObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          if (mutation.target.matches && mutation.target.matches(IMPRESSION_KEY_RULE)) {
            this.observeImpression(mutation.target);
          }

          return;
        }

        Array.from(mutation.addedNodes).forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.matches(IMPRESSION_KEY_RULE)) {
            this.observeImpression(node);
          }

          node.querySelectorAll(IMPRESSION_KEY_RULE).forEach((impression) => {
            this.observeImpression(impression);
          });
        });
      });
    });

    this.domObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-impression'],
    });

    // NEW: initial cards
    document.querySelectorAll(IMPRESSION_KEY_RULE).forEach((impression) => {
      this.observeImpression(impression);
    });

    document.addEventListener('click', (event) => {
      const nodes = document.querySelectorAll(IMPRESSION_KEY_RULE);
      [].forEach.call(nodes, (node) => {
        if (node.contains(event.target)) {
          const { id } = parseImpression(node);
          const product = this.cache[Number(id)];
          if (!product) return; // click before impression
          ga('ec:addProduct', product);
          ga('ec:setAction', 'click', { list: product.list });
        }
      });
    });
  };

  static fetchImpressionData = async (ids) => {
    try {
      if (!Array.isArray(ids)) {
        throw new Error('Wrong format of impression ids');
      }
      const res = await fetch('/api/analytics/product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      const products = await res.json();
      const gtagProductList = [];

      products.forEach((product) => {
        ga('ec:addImpression', product);
        gtagProductList.push({
          item_id: product.id,
          item_name: product.name,
          item_brand: product.brand,
          item_category: product.category,
        });
      });

      ga('send', 'event', 'ecommerce', 'impression', { nonInteraction: 1 });
      gtag('event', 'view_item_list', {
        item_list_id: 'listing',
        item_list_name: 'Projects Listing',
        items: gtagProductList,
      });
      return products;
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  sendImpressions = async (impressions) => {
    try {
      const ids = [];

      impressions.forEach((impression) => {
        const { id, ...other } = parseImpression(impression);
        this.cache[id] = other;
        ids.push(id);
      });

      const products = await Impressions.fetchImpressionData(ids);

      products.forEach((product) => {
        const id = product.id;
        this.cache[id] = { ...this.cache[id], ...product };
      });
    } catch (error) {
      console.warn(error);
    }
  };

  observeImpression = (impression) => {
    const key = getImpressionAttr(impression);
    if (this.sentImpressions.has(key)) {
      return;
    }

    this.visibilityObserver.observe(impression);
  };

  sendImpressionsDebounced = debounce(() => {
    this.pendingImpressions.forEach((impressions, listName) => {
      if (!impressions.length) {
        return;
      }

      this.pendingImpressions.delete(listName);
      this.sendImpressions(impressions);
    });
  }, DEBOUNCE_TIMEOUT_MS);
}

export default Impressions;
